// frontend/modules/template/components/ScriptTestingPanel.tsx
import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Button, 
    Typography, 
    Space, 
    Divider, 
    Alert, 
    Spin,
    Tag,
    Form,
    Tabs
} from 'antd';
import { ContextScriptEditor } from './ContextScriptEditor';
import { useScriptTesting } from '../hooks/useScriptTesting';
import { ContextTransformationResult } from '@/shared/api/model';
import { FormInstance } from 'antd/lib/form';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface ScriptTestingPanelProps {
    visible: boolean;
    onClose: () => void;
    scriptContent: string;
    sampleInput?: string;
    onTestSuccess?: (result: any) => void;
    form: FormInstance; // Pass the form instance from parent
}

export const ScriptTestingPanel: React.FC<ScriptTestingPanelProps> = ({
    visible,
    onClose,
    scriptContent,
    sampleInput = '{}',
    onTestSuccess,
    form
}) => {
    const { testScript, isTesting } = useScriptTesting();
    const [testResult, setTestResult] = useState<ContextTransformationResult | null>(null);
    const [testingError, setTestingError] = useState<string | null>(null);
    const [currentScriptContent, setCurrentScriptContent] = useState<string>('');
    
    // Reset test results when panel opens and log debug information
    useEffect(() => {
        if (visible) {
            // Get script content from form or props
            const formScriptContent = form.getFieldValue('scriptContent') || '';
            const effectiveScriptContent = scriptContent || formScriptContent;
            
            // Debug information
            console.log('ScriptTestingPanel opened with:', {
                propsScriptContent: scriptContent?.substring(0, 50) + (scriptContent?.length > 50 ? '...' : ''),
                propsScriptContentLength: scriptContent?.length,
                formScriptContent: formScriptContent?.substring(0, 50) + (formScriptContent?.length > 50 ? '...' : ''),
                formScriptContentLength: formScriptContent?.length,
                propsSampleInputLength: sampleInput?.length,
                formSampleInputLength: form.getFieldValue('sampleInput')?.length
            });
            
            // Store current script content for display
            setCurrentScriptContent(effectiveScriptContent);
            
            // Reset state
            setTestResult(null);
            setTestingError(null);
        }
    }, [visible, form, scriptContent, sampleInput]);
    
    const handleTest = async () => {
        try {
            // Reset previous results and errors
            setTestResult(null);
            setTestingError(null);
            
            // Get the current values from form and props
            const currentSampleInput = form.getFieldValue('sampleInput') || '{}';
            
            // Try to get script content from multiple sources to ensure we have something
            const effectiveScriptContent = currentScriptContent || 
                                          scriptContent || 
                                          form.getFieldValue('scriptContent') || '';
            
            console.log('Testing script with:', { 
                scriptContentFromProps: scriptContent?.length,
                scriptContentFromForm: form.getFieldValue('scriptContent')?.length,
                effectiveScriptContentLength: effectiveScriptContent?.length,
                testContextJsonLength: currentSampleInput?.length
            });
            
            // Ensure we have valid JSON for test context
            let testContextJson;
            try {
                // Parse and stringify to ensure valid JSON format
                testContextJson = JSON.stringify(JSON.parse(currentSampleInput));
            } catch (error) {
                setTestingError('Invalid JSON format in test context');
                return;
            }
            
            // Ensure scriptContent is not empty
            if (!effectiveScriptContent || effectiveScriptContent.trim() === '') {
                setTestingError('Script content cannot be empty');
                return;
            }
            
            // Execute the test
            const result = await testScript({
                scriptContent: effectiveScriptContent,
                testContextJson
            });

            if (result) {
                setTestResult(result);
                
                if (result.success && onTestSuccess && result.transformedContext) {
                    // Call the success callback with the transformed context
                    onTestSuccess(result.transformedContext);
                }
                
                if (!result.success) {
                    setTestingError(result.errorMessage || 'Unknown error occurred during script execution');
                }
            }
        } catch (error: any) {
            console.error('Script testing error:', error);
            setTestingError(error.message || 'An error occurred during testing');
        }
    };
    
    return (
        <Modal
            title="Test Context Script"
            open={visible}
            onCancel={onClose}
            width={900}
            footer={null}
        >
            <Tabs defaultActiveKey="input">
                <TabPane tab="Test Input" key="input">
                    <div>
                        <div className="mb-4">
                            <Text strong>Test Context JSON</Text>
                            <div className="mt-2">
                                <Form.Item
                                    name="sampleInput"
                                    noStyle
                                >
                                    <ContextScriptEditor
                                        language="json"
                                        height="200px"
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        
                        <div className="flex justify-end mb-4">
                            <Button type="primary" onClick={handleTest} loading={isTesting}>
                                Run Test
                            </Button>
                        </div>
                    </div>
                </TabPane>
                
                <TabPane tab="Script Content" key="script">
                    <div className="mb-4">
                        <Text strong>Current Script Content (Read-Only)</Text>
                        <div className="mt-2">
                            <ContextScriptEditor
                                language="javascript"
                                value={currentScriptContent}
                                height="300px"
                                readOnly
                            />
                        </div>
                    </div>
                </TabPane>
            </Tabs>
            
            {testingError && (
                <Alert 
                    message="Testing Error" 
                    description={testingError}
                    type="error" 
                    showIcon 
                    className="mb-4" 
                />
            )}
            
            {isTesting && (
                <div className="flex justify-center my-8">
                    <Spin tip="Testing script..." />
                </div>
            )}
            
            {testResult && testResult.success && (
                <>
                    <Divider />
                    <div>
                        <Space className="mb-2">
                            <Title level={4} className="mb-0">Result:</Title>
                            <Tag color="green">Success</Tag>
                        </Space>
                        
                        <div className="mb-4">
                            <Text>Transformed Context:</Text>
                            <ContextScriptEditor
                                language="json"
                                value={JSON.stringify(testResult.transformedContext, null, 2)}
                                height="300px"
                                readOnly
                            />
                        </div>
                    </div>
                </>
            )}
        </Modal>
    );
};
